import { useMemo, useState, useEffect, useCallback } from 'react'
import { StatusBadge } from './UI'
import { fmt, CREATOR_COLORS, AGENT_COLORS, CREATOR_SOCIALS, CREATOR_DEFAULT_AGENTS, creatorInitials, isPaidStatus } from '../lib/constants'
import { updateCreator, createCreator, apifyProxy } from '../lib/supabase'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LineChart, Line } from 'recharts'
import CreatorInsights from './CreatorInsights'
import CreatorAIInsights from './CreatorAIInsights'

const TIKTOK_ACTOR = 'GdWCkxBtKWOsKjdch'
const INSTA_ACTOR = 'shu8hvrXbJbY3Eb9W'

// Creators who have social analytics configured
// Handles identify which Apify run belongs to which creator (runs are filtered by input handle).
const SOCIAL_CREATORS = {
  'Steve':       { tiktok: TIKTOK_ACTOR, tiktokHandle: 'iamstevedaniels', instagram: INSTA_ACTOR, instagramHandle: 'iamstevedaniel' },
  'Citron Rose': { tiktok: TIKTOK_ACTOR, tiktokHandle: 'citron_rosee',    instagram: INSTA_ACTOR, instagramHandle: 'citron_rose'    },
}

function fmtNum(n) {
  if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M'
  if (n >= 1e3) return (n / 1e3).toFixed(1) + 'K'
  return n?.toLocaleString() || '0'
}

// Week-over-week change indicator
function Delta({ current, previous, suffix = '', invert = false }) {
  if (previous == null || previous === 0 || current == null) return null
  const pct = ((current - previous) / previous) * 100
  if (Math.abs(pct) < 0.5) return null
  const up = pct > 0
  const good = invert ? !up : up
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, marginLeft: 6, padding: '1px 5px', borderRadius: 4,
      background: good ? '#10b98118' : '#ef444418',
      color: good ? '#10b981' : '#ef4444',
    }}>
      {up ? '↑' : '↓'} {Math.abs(pct).toFixed(1)}%{suffix}
    </span>
  )
}

// Compute aggregate stats from raw post data for comparison
function computeTiktokAgg(data) {
  if (!data?.length) return null
  const totalViews = data.reduce((s, p) => s + (p.playCount || 0), 0)
  const totalLikes = data.reduce((s, p) => s + (p.diggCount || 0), 0)
  const totalShares = data.reduce((s, p) => s + (p.shareCount || 0), 0)
  const totalBookmarks = data.reduce((s, p) => s + (p.collectCount || 0), 0)
  const engagementRate = totalViews > 0 ? ((totalLikes + totalShares + data.reduce((s, p) => s + (p.commentCount || 0), 0)) / totalViews) * 100 : 0
  return { totalViews, totalLikes, totalShares, totalBookmarks, engagementRate }
}

function computeInstaAgg(data) {
  if (!data?.length) return null
  const totalLikes = data.reduce((s, p) => s + (p.likesCount || 0), 0)
  const totalComments = data.reduce((s, p) => s + (p.commentsCount || 0), 0)
  const avgLikes = Math.round(totalLikes / data.length)
  const avgComments = Math.round(totalComments / data.length)
  return { totalLikes, totalComments, avgLikes, avgComments }
}

const TT = ({ active, payload, label }) => active && payload?.length ? (
  <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>
    <div style={{ color: 'var(--text2)', marginBottom: 2 }}>{label}</div>
    {payload.map((p, i) => (
      <div key={i} style={{ fontWeight: 700, color: p.color || '#f97316' }}>{p.name}: {fmtNum(p.value)}</div>
    ))}
  </div>
) : null

export default function Creators({ deals, allDeals, creators: creatorsDb = [], onRefreshCreators, currentUser, viewMode, activeAgentName, isMobile, privacyMode }) {
  const hide = privacyMode
  const [editingId, setEditingId] = useState(null)
  const [editForm, setEditForm] = useState({ beacons_url: '', media_kit_url: '' })
  const [saving, setSaving] = useState(false)
  const [selectedCreator, setSelectedCreator] = useState(null)

  // Social analytics state
  const [tiktokData, setTiktokData] = useState(null)
  const [instaData, setInstaData] = useState(null)
  const [prevTiktokData, setPrevTiktokData] = useState(null)
  const [prevInstaData, setPrevInstaData] = useState(null)
  const [socialLoading, setSocialLoading] = useState(false)
  const [socialPlatform, setSocialPlatform] = useState('tiktok')

  const isAdmin = currentUser?.role === 'admin'
  const isCreatorRole = currentUser?.role === 'creator'
  const canEditLinks = isAdmin || currentUser?.role?.toLowerCase() === 'agent'
  // Use activeAgentName (from view switcher) if admin is viewing as an agent, otherwise use the logged-in agent's name
  const isAdminView = viewMode === 'admin'
  const effectiveAgent = activeAgentName || currentUser?.agent_name || ''
  // Creator's own name from profile for self-view.
  // agent_name is the field that matches deals.creator — full_name is a display
  // name and may carry a surname the deals don't use ("Mei-Anne Lemieux" vs 33
  // deals filed under "Mei-Anne"). Preferring full_name here silently showed
  // such a creator an empty tab, so agent_name comes first.
  const ownCreatorName = currentUser?.agent_name || currentUser?.full_name || ''

  // Auto-select own card when logged in as creator
  useEffect(() => {
    if (isCreatorRole && ownCreatorName && !selectedCreator) {
      setSelectedCreator(ownCreatorName)
    }
  }, [isCreatorRole, ownCreatorName, selectedCreator])

  const creatorStats = useMemo(() => {
    const map = {}
    // Creator self-view: only their own card
    if (isCreatorRole && ownCreatorName) {
      map[ownCreatorName] = { creator: ownCreatorName, revenue: 0, deals: 0, paid: 0, brands: new Set(), recentDeals: [], agentCounts: {} }
    } else if (isAdminView) {
      // Admin view = full roster from the DB (single source of truth),
      // plus legacy CREATOR_SOCIALS names not yet in the creators table.
      creatorsDb.forEach(c => {
        if (c.name) map[c.name] = { creator: c.name, revenue: 0, deals: 0, paid: 0, brands: new Set(), recentDeals: [], agentCounts: {} }
      })
      Object.keys(CREATOR_SOCIALS).forEach(name => {
        if (!map[name]) map[name] = { creator: name, revenue: 0, deals: 0, paid: 0, brands: new Set(), recentDeals: [], agentCounts: {} }
      })
    } else if (effectiveAgent) {
      // Only seed no-deal creators assigned to this agent
      Object.entries(CREATOR_DEFAULT_AGENTS).forEach(([name, agent]) => {
        if (agent === effectiveAgent) {
          map[name] = { creator: name, revenue: 0, deals: 0, paid: 0, brands: new Set(), recentDeals: [], agentCounts: {} }
        }
      })
    }
    deals.forEach(d => {
      if (!d.creator) return
      // Creator self-view: only their own deals
      if (isCreatorRole && d.creator !== ownCreatorName) return
      if (!map[d.creator]) map[d.creator] = { creator: d.creator, revenue: 0, deals: 0, paid: 0, brands: new Set(), recentDeals: [], agentCounts: {} }
      map[d.creator].revenue += d.deal_amount || 0
      map[d.creator].deals++
      if (isPaidStatus(d.status)) map[d.creator].paid += d.deal_amount || 0
      if (d.brand) map[d.creator].brands.add(d.brand)
      map[d.creator].recentDeals.push(d)
      const ag = d.agent || 'Steve'
      map[d.creator].agentCounts[ag] = (map[d.creator].agentCounts[ag] || 0) + 1
    })
    Object.values(map).forEach(c => {
      c.recentDeals = c.recentDeals.sort((a, b) => (b.date || '').localeCompare(a.date || '')).slice(0, 4)
      c.brands = [...c.brands]
      // Primary agent = most frequent agent for this creator
      // Primary agent from deals, or fallback to default assignment
      c.agent = Object.entries(c.agentCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || CREATOR_DEFAULT_AGENTS[c.creator] || null
    })
    // Sort: creators with deals first (by revenue), then no-deal creators alphabetically
    return Object.values(map).sort((a, b) => {
      if (a.deals > 0 && b.deals === 0) return -1
      if (a.deals === 0 && b.deals > 0) return 1
      if (a.deals === 0 && b.deals === 0) return a.creator.localeCompare(b.creator)
      return b.revenue - a.revenue
    })
  }, [deals, isAdminView, effectiveAgent, creatorsDb, isCreatorRole, ownCreatorName])

  const dbMap = useMemo(() => {
    const m = {}
    creatorsDb.forEach(c => { m[c.name] = c })
    return m
  }, [creatorsDb])

  // Social links: DB handles (synced from emane.agency) first, legacy hardcoded list as fallback
  const getSocials = useCallback((name) => {
    const db = dbMap[name]
    const instagram = db?.instagram_handle
      ? `https://www.instagram.com/${db.instagram_handle}/`
      : CREATOR_SOCIALS[name]?.instagram
    const tiktok = db?.tiktok_handle
      ? `https://www.tiktok.com/@${db.tiktok_handle}`
      : CREATOR_SOCIALS[name]?.tiktok
    return (instagram || tiktok) ? { instagram, tiktok } : null
  }, [dbMap])

  // Fetch social data — filters runs by the creator's handle so each creator sees only their own data
  const fetchRuns = useCallback(async (actorId, handle) => {
    try {
      // If a handle is provided, use runs-by-handle to filter to this creator's runs only.
      // Otherwise fall back to latest-runs (legacy behaviour).
      const runsJson = handle
        ? await apifyProxy({ action: 'runs-by-handle', actorId, handle, limit: 2 })
        : await apifyProxy({ action: 'latest-runs', actorId, limit: 2 })
      const runs = runsJson?.data?.items || []
      const fetchDataset = async (run) => {
        if (!run) return null
        const items = await apifyProxy({ action: 'run-dataset', runId: run.id })
        return { items, run }
      }
      const [current, previous] = await Promise.all([fetchDataset(runs[0]), fetchDataset(runs[1])])
      return { current, previous }
    } catch (e) {
      console.error('Apify fetch error:', e)
      return { current: null, previous: null }
    }
  }, [])

  useEffect(() => {
    if (!selectedCreator || !SOCIAL_CREATORS[selectedCreator]) {
      setTiktokData(null); setInstaData(null)
      setPrevTiktokData(null); setPrevInstaData(null)
      return
    }
    const config = SOCIAL_CREATORS[selectedCreator]
    setSocialLoading(true)
    Promise.all([
      config.tiktok ? fetchRuns(config.tiktok, config.tiktokHandle) : Promise.resolve({ current: null, previous: null }),
      config.instagram ? fetchRuns(config.instagram, config.instagramHandle) : Promise.resolve({ current: null, previous: null }),
    ]).then(([tk, ig]) => {
      if (tk.current) setTiktokData(tk.current.items)
      if (tk.previous) setPrevTiktokData(tk.previous.items)
      if (ig.current) setInstaData(ig.current.items)
      if (ig.previous) setPrevInstaData(ig.previous.items)
      setSocialLoading(false)
    })
  }, [selectedCreator, fetchRuns])

  const [editingName, setEditingName] = useState(null)
  const startEdit = (dbCreator, creatorName) => {
    setEditingId(dbCreator?.id || null)
    setEditingName(creatorName || dbCreator?.name || null)
    setEditForm({ beacons_url: dbCreator?.beacons_url || '', media_kit_url: dbCreator?.media_kit_url || '' })
  }

  const saveEdit = async () => {
    setSaving(true)
    let id = editingId
    // If creator doesn't exist in DB yet, create them first
    if (!id && editingName) {
      const { data } = await createCreator(editingName)
      if (data?.[0]) id = data[0].id
    }
    if (id) {
      await updateCreator(id, { beacons_url: editForm.beacons_url || null, media_kit_url: editForm.media_kit_url || null })
    }
    setSaving(false)
    setEditingId(null)
    setEditingName(null)
    if (onRefreshCreators) onRefreshCreators()
  }

  const hasSocial = selectedCreator && SOCIAL_CREATORS[selectedCreator]

  const linkBtn = { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, textDecoration: 'none', border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--text)', cursor: 'pointer', transition: 'all 0.15s' }
  const inp = { width: '100%', padding: '6px 8px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: 11, outline: 'none', boxSizing: 'border-box' }
  const card = { background: 'var(--gradient-card, var(--bg2))', border: '1px solid var(--card-border, var(--border))', borderRadius: 'var(--radius-lg, 12px)', padding: isMobile ? 16 : 20, boxShadow: 'var(--shadow-sm, none)' }

  // ─── DETAIL VIEW ────────────────────────────────────────
  if (selectedCreator) {
    const cs = creatorStats.find(c => c.creator === selectedCreator)
    if (!cs) { setSelectedCreator(null); return null }
    const color = CREATOR_COLORS[selectedCreator] || '#6b7280'
    const dbCreator = dbMap[selectedCreator]
    const paidPct = cs.revenue > 0 ? (cs.paid / cs.revenue) * 100 : 0

    // All deals for this creator (from allDeals to get full picture)
    const creatorDeals = (allDeals || deals).filter(d => d.creator === selectedCreator).sort((a, b) => (b.date || '').localeCompare(a.date || ''))

    // Status breakdown
    const statusBreakdown = {}
    creatorDeals.forEach(d => { statusBreakdown[d.status] = (statusBreakdown[d.status] || 0) + 1 })

    // Social stats
    const tiktokStats = tiktokData?.length ? (() => {
      const totalViews = tiktokData.reduce((s, p) => s + (p.playCount || 0), 0)
      const totalLikes = tiktokData.reduce((s, p) => s + (p.diggCount || 0), 0)
      const totalShares = tiktokData.reduce((s, p) => s + (p.shareCount || 0), 0)
      const totalComments = tiktokData.reduce((s, p) => s + (p.commentCount || 0), 0)
      const totalBookmarks = tiktokData.reduce((s, p) => s + (p.collectCount || 0), 0)
      const engagementRate = totalViews > 0 ? ((totalLikes + totalComments + totalShares + totalBookmarks) / totalViews * 100) : 0
      const byMonth = {}
      tiktokData.forEach(p => {
        const d = new Date(p.createTimeISO)
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
        if (!byMonth[key]) byMonth[key] = { month: key, views: 0, likes: 0, posts: 0 }
        byMonth[key].views += p.playCount || 0
        byMonth[key].likes += p.diggCount || 0
        byMonth[key].posts++
      })
      const monthlyData = Object.values(byMonth).sort((a, b) => a.month.localeCompare(b.month))
      const topPosts = [...tiktokData].sort((a, b) => (b.playCount || 0) - (a.playCount || 0)).slice(0, 5)
      const durationBuckets = { '0-15s': [], '15-30s': [], '30-60s': [], '60s+': [] }
      tiktokData.forEach(p => {
        const dur = p.videoMeta?.duration || 0
        if (dur <= 15) durationBuckets['0-15s'].push(p)
        else if (dur <= 30) durationBuckets['15-30s'].push(p)
        else if (dur <= 60) durationBuckets['30-60s'].push(p)
        else durationBuckets['60s+'].push(p)
      })
      const durationData = Object.entries(durationBuckets).map(([range, posts]) => ({
        range, count: posts.length,
        avgViews: posts.length ? Math.round(posts.reduce((s, p) => s + (p.playCount || 0), 0) / posts.length) : 0,
      }))
      return { totalViews, totalLikes, totalShares, totalComments, totalBookmarks, engagementRate, monthlyData, topPosts, durationData, postCount: tiktokData.length }
    })() : null

    const instaStats = instaData?.length ? (() => {
      const totalLikes = instaData.reduce((s, p) => s + (p.likesCount || 0), 0)
      const totalComments = instaData.reduce((s, p) => s + (p.commentsCount || 0), 0)
      const avgLikes = totalLikes / instaData.length
      const avgComments = totalComments / instaData.length
      const byType = {}
      instaData.forEach(p => {
        const t = p.type || 'Unknown'
        if (!byType[t]) byType[t] = { type: t, count: 0, likes: 0 }
        byType[t].count++
        byType[t].likes += p.likesCount || 0
      })
      const typeData = Object.values(byType).sort((a, b) => b.likes - a.likes)
      const byMonth = {}
      instaData.forEach(p => {
        const d = new Date(p.timestamp)
        if (isNaN(d.getTime())) return
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
        if (!byMonth[key]) byMonth[key] = { month: key, likes: 0, comments: 0, posts: 0 }
        byMonth[key].likes += p.likesCount || 0
        byMonth[key].comments += p.commentsCount || 0
        byMonth[key].posts++
      })
      const monthlyData = Object.values(byMonth).sort((a, b) => a.month.localeCompare(b.month))
      const topPosts = [...instaData].sort((a, b) => (b.likesCount || 0) - (a.likesCount || 0)).slice(0, 5)
      const hashtagMap = {}
      instaData.forEach(p => {
        (p.hashtags || []).forEach(h => {
          if (!hashtagMap[h]) hashtagMap[h] = { tag: h, count: 0, totalLikes: 0 }
          hashtagMap[h].count++
          hashtagMap[h].totalLikes += p.likesCount || 0
        })
      })
      Object.values(hashtagMap).forEach(h => { h.avgLikes = Math.round(h.totalLikes / h.count) })
      const topHashtags = Object.values(hashtagMap).sort((a, b) => b.count - a.count).slice(0, 10)
      return { totalLikes, totalComments, avgLikes, avgComments, monthlyData, topPosts, typeData, topHashtags, postCount: instaData.length }
    })() : null

    // Previous run aggregate stats for comparison
    const prevTiktokAgg = computeTiktokAgg(prevTiktokData)
    const prevInstaAgg = computeInstaAgg(prevInstaData)

    const socialStats = socialPlatform === 'tiktok' ? tiktokStats : instaStats

    return (
      <div className="fade-in">
        {/* Back button + header */}
        <button onClick={() => setSelectedCreator(null)} style={{
          background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer',
          fontSize: 13, fontWeight: 600, padding: '4px 0', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <span style={{ fontSize: 16 }}>←</span> Back to Roster
        </button>

        {/* Creator header card */}
        <div style={{ ...card, borderTop: `3px solid ${color}`, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', gap: 16, flexDirection: isMobile ? 'column' : 'row' }}>
            {dbCreator?.photo_url ? (
              <img src={dbCreator.photo_url} alt={selectedCreator} style={{
                width: 60, height: 60, borderRadius: '50%', flexShrink: 0,
                objectFit: 'cover', border: `2px solid ${color}`,
              }} />
            ) : (
              <div style={{
                width: 60, height: 60, borderRadius: '50%', flexShrink: 0,
                background: color + '18', border: `2px solid ${color}`,
                color, fontWeight: 700, fontSize: 20, display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: 'var(--font-display)'
              }}>{creatorInitials(selectedCreator)}</div>
            )}
            <div style={{ flex: 1 }}>
              <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, letterSpacing: '-0.5px', margin: 0 }}>{selectedCreator}</h1>
              <p style={{ color: 'var(--text2)', fontSize: 13, margin: '2px 0 0' }}>
                {cs.deals} deals . {cs.brands.length} brands . {hide ? '---' : fmt(cs.revenue)} pipeline
                {!hide && cs.deals > 0 && ` . ${fmt(cs.revenue / cs.deals)} avg`}
              </p>
            </div>
            {!hide && (
              <div style={{ display: 'flex', gap: 14, flexShrink: 0 }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'var(--font-display)', color }}>{hide ? '•••••' : fmt(cs.revenue)}</div>
                  <div style={{ fontSize: 10, color: 'var(--text3)' }}>Total Pipeline</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'var(--font-display)', color: '#10b981' }}>{hide ? '•••••' : fmt(cs.paid)}</div>
                  <div style={{ fontSize: 10, color: 'var(--text3)' }}>Collected</div>
                </div>
              </div>
            )}
          </div>

          {/* Collection progress bar */}
          {!hide && (
            <div style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4 }}>
                <span style={{ color: 'var(--text3)' }}>Collection Progress</span>
                <span style={{ color: '#10b981', fontWeight: 600 }}>{paidPct.toFixed(0)}%</span>
              </div>
              <div style={{ background: 'var(--bg3)', borderRadius: 4, height: 6, overflow: 'hidden' }}>
                <div style={{ background: '#10b981', height: '100%', width: `${paidPct}%`, borderRadius: 4, transition: 'width 0.3s' }} />
              </div>
            </div>
          )}

          {/* Links */}
          <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {getSocials(selectedCreator)?.instagram && (
              <a href={getSocials(selectedCreator).instagram} target="_blank" rel="noopener noreferrer" style={{ ...linkBtn, color: '#E1306C', borderColor: '#E1306C30' }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                Instagram
              </a>
            )}
            {getSocials(selectedCreator)?.tiktok && (
              <a href={getSocials(selectedCreator).tiktok} target="_blank" rel="noopener noreferrer" style={{ ...linkBtn, color: 'var(--text)', borderColor: 'var(--border)' }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.75a8.18 8.18 0 004.77 1.52V6.84a4.84 4.84 0 01-1-.15z"/></svg>
                TikTok
              </a>
            )}
            {dbCreator?.beacons_url && <a href={dbCreator.beacons_url} target="_blank" rel="noopener noreferrer" style={{ ...linkBtn, color: '#f97316', borderColor: '#f9731630' }}>Beacons</a>}
            {dbCreator?.media_kit_url && <a href={dbCreator.media_kit_url} target="_blank" rel="noopener noreferrer" style={{ ...linkBtn, color: '#8b5cf6', borderColor: '#8b5cf630' }}>Media Kit</a>}
            {(() => {
              // website_creators embeds as an array (FK lives on the website side)
              const site = Array.isArray(dbCreator?.website_creators) ? dbCreator.website_creators[0] : dbCreator?.website_creators
              if (!site?.slug) return null
              return (
                <a href={`https://www.emane.agency/en/creators/${site.slug}`} target="_blank" rel="noopener noreferrer"
                   style={{ ...linkBtn, color: '#10b981', borderColor: '#10b98130' }}
                   title={site.published === false ? 'Profile exists but is unpublished' : 'View public profile on emane.agency'}>
                  🌐 Émane profile{site.published === false ? ' (draft)' : ''}
                </a>
              )
            })()}
          </div>
        </div>

        {/* Deal stats row */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : `repeat(${Math.min(Object.keys(statusBreakdown).length, 5)},1fr)`, gap: 10, marginBottom: 20 }}>
          {Object.entries(statusBreakdown).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([status, count]) => (
            <div key={status} style={{ ...card, padding: '12px 14px', textAlign: 'center' }}>
              <div style={{ marginBottom: 4 }}><StatusBadge status={status} /></div>
              <div style={{ fontSize: 20, fontWeight: 700, fontFamily: 'var(--font-display)' }}>{count}</div>
            </div>
          ))}
        </div>

        {/* Brands */}
        <div style={{ ...card, marginBottom: 20 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Brands ({cs.brands.length})</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {cs.brands.map(b => (
              <span key={b} style={{ fontSize: 12, padding: '4px 10px', background: color + '12', color, borderRadius: 6, border: `1px solid ${color}25`, fontWeight: 500 }}>{b}</span>
            ))}
          </div>
        </div>

        {/* Recent deals */}
        <div style={{ ...card, marginBottom: 20, padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', fontWeight: 700, fontSize: 14 }}>
            Recent Deals
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: isMobile ? 500 : 'auto' }}>
              <thead>
                <tr style={{ background: 'var(--bg3)' }}>
                  {['Brand', 'Status', 'Amount', 'Month'].map(h => (
                    <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid var(--border)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {creatorDeals.slice(0, 10).map((d, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(249,115,22,0.04)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <td style={{ padding: '10px 14px', fontWeight: 500 }}>{d.brand}</td>
                    <td style={{ padding: '10px 14px' }}><StatusBadge status={d.status} /></td>
                    <td style={{ padding: '10px 14px', fontWeight: 700, fontFamily: 'var(--font-display)' }}>{hide ? '---' : fmt(d.deal_amount)}</td>
                    <td style={{ padding: '10px 14px', color: 'var(--text3)' }}>{d.month || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ─── SOCIAL ANALYTICS SECTION ───────────────────── */}
        {hasSocial && (
          <div style={{ borderTop: '2px solid var(--border)', paddingTop: 20, marginTop: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', marginBottom: 16, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 10 : 0 }}>
              <div>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, letterSpacing: '-0.3px', margin: 0 }}>Social Analytics</h2>
                <p style={{ color: 'var(--text2)', fontSize: 12, marginTop: 2 }}>
                  {socialLoading ? 'Loading social data...' :
                    socialPlatform === 'tiktok'
                      ? `${tiktokData?.length || 0} TikTok posts analyzed${prevTiktokData ? ' · vs previous run' : ''}`
                      : `${instaData?.length || 0} Instagram posts analyzed${prevInstaData ? ' · vs previous run' : ''}`
                  }
                </p>
              </div>
              <div style={{ display: 'flex', background: 'var(--bg2)', borderRadius: 10, border: '1px solid var(--border)', overflow: 'hidden' }}>
                {[
                  { id: 'tiktok', label: 'TikTok', icon: '♪', disabled: !tiktokData },
                  { id: 'instagram', label: 'Instagram', icon: '◎', disabled: !instaData }
                ].map(p => (
                  <button key={p.id} onClick={() => !p.disabled && setSocialPlatform(p.id)} disabled={p.disabled} style={{
                    padding: '7px 16px', fontSize: 12, fontWeight: 600, border: 'none', cursor: p.disabled ? 'not-allowed' : 'pointer',
                    background: socialPlatform === p.id ? 'var(--accent, #f97316)' : 'transparent',
                    color: socialPlatform === p.id ? '#fff' : p.disabled ? 'var(--text3)' : 'var(--text)',
                    transition: 'all 0.2s', opacity: p.disabled ? 0.4 : 1,
                  }}>
                    {p.icon} {p.label}
                  </button>
                ))}
              </div>
            </div>

            {socialLoading && (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>
                <div className="loading" style={{ fontSize: 13 }}>Fetching social data from Apify...</div>
              </div>
            )}

            {!socialLoading && socialStats && (
              <>
                {/* KPI Cards */}
                {socialPlatform === 'tiktok' && tiktokStats && (
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(5,1fr)', gap: 10, marginBottom: 16 }}>
                    {[
                      { label: 'Total Views', value: fmtNum(tiktokStats.totalViews), color: '#f97316', cur: tiktokStats.totalViews, prev: prevTiktokAgg?.totalViews },
                      { label: 'Total Likes', value: fmtNum(tiktokStats.totalLikes), color: '#ef4444', cur: tiktokStats.totalLikes, prev: prevTiktokAgg?.totalLikes },
                      { label: 'Total Shares', value: fmtNum(tiktokStats.totalShares), color: '#3b82f6', cur: tiktokStats.totalShares, prev: prevTiktokAgg?.totalShares },
                      { label: 'Bookmarks', value: fmtNum(tiktokStats.totalBookmarks), color: '#8b5cf6', cur: tiktokStats.totalBookmarks, prev: prevTiktokAgg?.totalBookmarks },
                      { label: 'Engagement', value: tiktokStats.engagementRate.toFixed(1) + '%', color: '#10b981', cur: tiktokStats.engagementRate, prev: prevTiktokAgg?.engagementRate },
                    ].map(({ label, value, color: c, cur, prev }) => (
                      <div key={label} style={{ ...card, borderTop: `2px solid ${c}`, padding: '12px 14px' }}>
                        <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>{label}</div>
                        <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'var(--font-display)', color: c }}>
                          {value}
                          <Delta current={cur} previous={prev} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {socialPlatform === 'instagram' && instaStats && (
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 10, marginBottom: 16 }}>
                    {[
                      { label: 'Total Likes', value: fmtNum(instaStats.totalLikes), color: '#ef4444', cur: instaStats.totalLikes, prev: prevInstaAgg?.totalLikes },
                      { label: 'Total Comments', value: fmtNum(instaStats.totalComments), color: '#3b82f6', cur: instaStats.totalComments, prev: prevInstaAgg?.totalComments },
                      { label: 'Avg Likes/Post', value: fmtNum(Math.round(instaStats.avgLikes)), color: '#f97316', cur: instaStats.avgLikes, prev: prevInstaAgg?.avgLikes },
                      { label: 'Avg Comments', value: fmtNum(Math.round(instaStats.avgComments)), color: '#10b981', cur: instaStats.avgComments, prev: prevInstaAgg?.avgComments },
                    ].map(({ label, value, color: c, cur, prev }) => (
                      <div key={label} style={{ ...card, borderTop: `2px solid ${c}`, padding: '12px 14px' }}>
                        <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>{label}</div>
                        <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'var(--font-display)', color: c }}>
                          {value}
                          <Delta current={cur} previous={prev} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Charts */}
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14, marginBottom: 14 }}>
                  {socialStats?.monthlyData?.length > 0 && (
                    <div style={card}>
                      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>
                        {socialPlatform === 'tiktok' ? 'Views by Month' : 'Likes by Month'}
                      </div>
                      <ResponsiveContainer width="100%" height={160}>
                        <LineChart data={socialStats.monthlyData}>
                          <XAxis dataKey="month" tick={{ fill: '#55575f', fontSize: 10 }} axisLine={false} tickLine={false}
                            tickFormatter={v => { const [y, m] = v.split('-'); return ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][parseInt(m)-1] + ' ' + y.slice(2) }} />
                          <YAxis hide />
                          <Tooltip content={<TT />} />
                          {socialPlatform === 'tiktok'
                            ? <Line type="monotone" dataKey="views" stroke="#f97316" strokeWidth={2} dot={{ r: 3, fill: '#f97316' }} name="Views" />
                            : <Line type="monotone" dataKey="likes" stroke="#ef4444" strokeWidth={2} dot={{ r: 3, fill: '#ef4444' }} name="Likes" />
                          }
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {socialPlatform === 'tiktok' && tiktokStats?.durationData && (
                    <div style={card}>
                      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>Avg Views by Duration</div>
                      <ResponsiveContainer width="100%" height={160}>
                        <BarChart data={tiktokStats.durationData} barCategoryGap="30%">
                          <XAxis dataKey="range" tick={{ fill: '#55575f', fontSize: 11 }} axisLine={false} tickLine={false} />
                          <YAxis hide />
                          <Tooltip content={<TT />} />
                          <Bar dataKey="avgViews" radius={[6, 6, 0, 0]} name="Avg Views">
                            {tiktokStats.durationData.map((_, i) => <Cell key={i} fill={['#f97316', '#f59e0b', '#10b981', '#3b82f6'][i]} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {socialPlatform === 'instagram' && instaStats?.typeData && (
                    <div style={card}>
                      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>Posts by Type</div>
                      <ResponsiveContainer width="100%" height={160}>
                        <BarChart data={instaStats.typeData} barCategoryGap="30%">
                          <XAxis dataKey="type" tick={{ fill: '#55575f', fontSize: 11 }} axisLine={false} tickLine={false} />
                          <YAxis hide />
                          <Tooltip content={<TT />} />
                          <Bar dataKey="likes" radius={[6, 6, 0, 0]} name="Total Likes">
                            {instaStats.typeData.map((_, i) => <Cell key={i} fill={['#ef4444', '#f97316', '#3b82f6', '#8b5cf6'][i % 4]} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>

                {/* Top Posts */}
                {socialStats?.topPosts?.length > 0 && (
                  <div style={{ ...card, overflow: 'hidden', marginBottom: 14, padding: 0 }}>
                    <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', fontWeight: 700, fontSize: 14 }}>
                      Top Performing Posts
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: isMobile ? 600 : 'auto' }}>
                        <thead>
                          <tr style={{ background: 'var(--bg3)' }}>
                            {socialPlatform === 'tiktok'
                              ? ['#', 'Content', 'Views', 'Likes', 'Shares', 'Duration'].map(h => (
                                  <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid var(--border)' }}>{h}</th>
                                ))
                              : ['#', 'Content', 'Likes', 'Comments', 'Type'].map(h => (
                                  <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid var(--border)' }}>{h}</th>
                                ))
                            }
                          </tr>
                        </thead>
                        <tbody>
                          {socialStats.topPosts.map((p, i) => (
                            <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}
                              onMouseEnter={e => e.currentTarget.style.background = 'rgba(249,115,22,0.04)'}
                              onMouseLeave={e => e.currentTarget.style.background = ''}>
                              <td style={{ padding: '10px 14px', color: 'var(--text3)', fontWeight: 600 }}>{i + 1}</td>
                              <td style={{ padding: '10px 14px', maxWidth: 280 }}>
                                <a href={socialPlatform === 'tiktok' ? p.webVideoUrl : p.url} target="_blank" rel="noopener noreferrer"
                                  style={{ color: 'var(--text)', textDecoration: 'none', fontWeight: 500 }}>
                                  {(socialPlatform === 'tiktok' ? p.text : p.caption || p.alt || 'Post')?.substring(0, 70)}
                                  {(socialPlatform === 'tiktok' ? p.text : p.caption || p.alt || '')?.length > 70 ? '...' : ''}
                                </a>
                              </td>
                              {socialPlatform === 'tiktok' ? (
                                <>
                                  <td style={{ padding: '10px 14px', fontWeight: 700, color: '#f97316', fontFamily: 'var(--font-display)' }}>{fmtNum(p.playCount)}</td>
                                  <td style={{ padding: '10px 14px', color: '#ef4444', fontWeight: 600 }}>{fmtNum(p.diggCount)}</td>
                                  <td style={{ padding: '10px 14px', color: '#3b82f6' }}>{fmtNum(p.shareCount)}</td>
                                  <td style={{ padding: '10px 14px', color: 'var(--text2)' }}>{p.videoMeta?.duration || 0}s</td>
                                </>
                              ) : (
                                <>
                                  <td style={{ padding: '10px 14px', fontWeight: 700, color: '#ef4444', fontFamily: 'var(--font-display)' }}>{fmtNum(p.likesCount)}</td>
                                  <td style={{ padding: '10px 14px', color: '#3b82f6' }}>{fmtNum(p.commentsCount)}</td>
                                  <td style={{ padding: '10px 14px' }}>
                                    <span style={{ padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                                      background: p.type === 'Video' ? 'rgba(239,68,68,0.1)' : 'rgba(59,130,246,0.1)',
                                      color: p.type === 'Video' ? '#ef4444' : '#3b82f6'
                                    }}>{p.type}</span>
                                  </td>
                                </>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Instagram Hashtags */}
                {socialPlatform === 'instagram' && instaStats?.topHashtags?.length > 0 && (
                  <div style={{ ...card, overflow: 'hidden', padding: 0, marginBottom: 14 }}>
                    <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', fontWeight: 700, fontSize: 14 }}>
                      Top Hashtags
                    </div>
                    <div style={{ padding: 14, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {instaStats.topHashtags.map(h => (
                        <div key={h.tag} style={{
                          padding: '5px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                          background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.2)', color: '#f97316',
                          display: 'flex', alignItems: 'center', gap: 6
                        }}>
                          #{h.tag}
                          <span style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 400 }}>{h.count}x . avg {fmtNum(h.avgLikes)} likes</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ─── CREATOR INSIGHTS (Actionable) ─────────────── */}
                <CreatorInsights tiktokData={tiktokData} instaData={instaData} isMobile={isMobile} />

                {/* ─── AI STRATEGIC RECOMMENDATIONS (Claude-powered) ──── */}
                <CreatorAIInsights creatorName={selectedCreator} tiktokData={tiktokData} instaData={instaData} isMobile={isMobile} />
              </>
            )}

            {!socialLoading && !tiktokData && !instaData && (
              <div style={{ textAlign: 'center', padding: 30, color: 'var(--text3)', fontSize: 13 }}>
                No social data available. Make sure the Apify scrapers have been run.
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  // ─── GRID VIEW (default) ────────────────────────────────
  return (
    <div className="fade-in">
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 700, letterSpacing: '-0.5px' }}>
          Creator Roster
          {hide && <span style={{ marginLeft: 10, fontSize: 12, fontWeight: 600, color: '#8b5cf6', background: 'rgba(139,92,246,0.12)', padding: '3px 8px', borderRadius: 6 }}>Privacy</span>}
        </h1>
        <p style={{ color: 'var(--text2)', fontSize: 13, marginTop: 3 }}>{creatorStats.length} creators . 2026</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)', gap: isMobile ? 10 : 14 }}>
        {creatorStats.map(({ creator, revenue, deals: dealCount, paid, brands, recentDeals, agent: primaryAgent }) => {
          const color = CREATOR_COLORS[creator] || '#6b7280'
          const paidPct = revenue > 0 ? (paid / revenue) * 100 : 0
          const dbCreator = dbMap[creator]
          const isEditing = dbCreator ? editingId === dbCreator.id : editingName === creator
          const hasSocialBadge = !!SOCIAL_CREATORS[creator]

          return (
            <div key={creator} onClick={() => !isEditing && setSelectedCreator(creator)} style={{
              background: 'var(--bg2)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-lg)', padding: 20, borderTop: `2px solid ${color}`,
              cursor: isEditing ? 'default' : 'pointer', transition: 'all 0.15s ease',
            }}
              onMouseEnter={e => { if (!isEditing) { e.currentTarget.style.borderColor = color; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 4px 20px ${color}20` } }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                {dbCreator?.photo_url ? (
                  <img src={dbCreator.photo_url} alt={creator} loading="lazy" style={{
                    width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                    objectFit: 'cover', border: `2px solid ${color}`,
                  }} />
                ) : (
                  <div style={{
                    width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                    background: color + '18', border: `2px solid ${color}`,
                    color, fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontFamily: 'var(--font-display)'
                  }}>{creatorInitials(creator)}</div>
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 16, fontFamily: 'var(--font-display)', display: 'flex', alignItems: 'center', gap: 6 }}>
                    {creator}
                    {hasSocialBadge && (
                      <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: 'rgba(249,115,22,0.12)', color: '#f97316', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Social</span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', display: 'flex', alignItems: 'center', gap: 6 }}>
                    {dealCount > 0 ? `${dealCount} deals` : 'No deals yet'}
                    {primaryAgent && (
                      <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3, background: (AGENT_COLORS[primaryAgent] || '#6b7280') + '18', color: AGENT_COLORS[primaryAgent] || '#6b7280' }}>
                        {primaryAgent}
                      </span>
                    )}
                    {getSocials(creator) && (
                      <span style={{ display: 'inline-flex', gap: 4, marginLeft: 2 }}>
                        {getSocials(creator).instagram && (
                          <a href={getSocials(creator).instagram} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} title="Instagram" style={{ color: '#E1306C', fontSize: 13, lineHeight: 1, textDecoration: 'none', opacity: 0.8, transition: 'opacity 0.15s' }} onMouseEnter={e => e.currentTarget.style.opacity = 1} onMouseLeave={e => e.currentTarget.style.opacity = 0.8}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                          </a>
                        )}
                        {getSocials(creator).tiktok && (
                          <a href={getSocials(creator).tiktok} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} title="TikTok" style={{ color: 'var(--text)', fontSize: 13, lineHeight: 1, textDecoration: 'none', opacity: 0.8, transition: 'opacity 0.15s' }} onMouseEnter={e => e.currentTarget.style.opacity = 1} onMouseLeave={e => e.currentTarget.style.opacity = 0.8}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.75a8.18 8.18 0 004.77 1.52V6.84a4.84 4.84 0 01-1-.15z"/></svg>
                          </a>
                        )}
                      </span>
                    )}
                  </div>
                </div>
                {!hide && (
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 700, fontSize: 18, fontFamily: 'var(--font-display)', color }}>{hide ? '•••••' : fmt(revenue)}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)' }}>total</div>
                  </div>
                )}
              </div>

              {!hide && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 6 }}>
                    <span style={{ color: 'var(--text3)' }}>Avg per deal</span>
                    <span style={{ color: 'var(--text2)', fontWeight: 600 }}>
                      {hide ? '•••••' : dealCount ? fmt(revenue / dealCount) : '--'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4 }}>
                    <span style={{ color: 'var(--text3)' }}>Collected</span>
                    <span style={{ color: '#10b981', fontWeight: 600 }}>{hide ? '•••••' : fmt(paid)}</span>
                  </div>
                  <div style={{ background: 'var(--bg3)', borderRadius: 4, height: 5, overflow: 'hidden' }}>
                    <div style={{ background: '#10b981', height: '100%', width: `${paidPct}%`, borderRadius: 4 }} />
                  </div>
                </div>
              )}

              {/* Beacons & Media Kit Links */}
              <div style={{ marginBottom: 12, padding: '10px 0', borderTop: '1px solid var(--border)' }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Links</div>
                  {canEditLinks && !isEditing && (
                    <button onClick={(e) => { e.stopPropagation(); startEdit(dbCreator, creator) }} style={{ background: 'none', border: 'none', color: 'var(--text3)', fontSize: 11, cursor: 'pointer', padding: '2px 6px' }}>Edit</button>
                  )}
                </div>

                {isEditing ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div>
                      <label style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 2, display: 'block' }}>Beacons URL</label>
                      <input value={editForm.beacons_url} onChange={e => setEditForm(f => ({ ...f, beacons_url: e.target.value }))} style={inp} placeholder="https://beacons.ai/..." />
                    </div>
                    <div>
                      <label style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 2, display: 'block' }}>Media Kit URL</label>
                      <input value={editForm.media_kit_url} onChange={e => setEditForm(f => ({ ...f, media_kit_url: e.target.value }))} style={inp} placeholder="https://..." />
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                      <button onClick={saveEdit} disabled={saving} style={{ ...linkBtn, background: 'var(--accent)', color: '#fff', border: 'none', fontSize: 11 }}>
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button onClick={() => { setEditingId(null); setEditingName(null) }} style={{ ...linkBtn, fontSize: 11 }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {getSocials(creator)?.instagram && (
                      <a href={getSocials(creator).instagram} target="_blank" rel="noopener noreferrer" style={{ ...linkBtn, color: '#E1306C', borderColor: '#E1306C30' }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                        IG
                      </a>
                    )}
                    {getSocials(creator)?.tiktok && (
                      <a href={getSocials(creator).tiktok} target="_blank" rel="noopener noreferrer" style={{ ...linkBtn, color: 'var(--text)', borderColor: 'var(--border)' }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.75a8.18 8.18 0 004.77 1.52V6.84a4.84 4.84 0 01-1-.15z"/></svg>
                        TT
                      </a>
                    )}
                    {dbCreator?.beacons_url ? (
                      <a href={dbCreator.beacons_url} target="_blank" rel="noopener noreferrer" style={{ ...linkBtn, color: '#f97316', borderColor: '#f9731630' }}>
                        Beacons
                      </a>
                    ) : (
                      <span style={{ ...linkBtn, opacity: 0.4, cursor: 'default' }}>Beacons</span>
                    )}
                    {dbCreator?.media_kit_url ? (
                      <a href={dbCreator.media_kit_url} target="_blank" rel="noopener noreferrer" style={{ ...linkBtn, color: '#8b5cf6', borderColor: '#8b5cf630' }}>
                        Media Kit
                      </a>
                    ) : (
                      <span style={{ ...linkBtn, opacity: 0.4, cursor: 'default' }}>Media Kit</span>
                    )}
                  </div>
                )}
              </div>

              {/* Brands */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Brands</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {brands.slice(0, 6).map(b => (
                    <span key={b} style={{ fontSize: 11, padding: '2px 7px', background: color + '12', color, borderRadius: 4, border: `1px solid ${color}25`, fontWeight: 500 }}>{b}</span>
                  ))}
                  {brands.length > 6 && <span style={{ fontSize: 11, color: 'var(--text3)' }}>+{brands.length - 6}</span>}
                </div>
              </div>

              {/* Recent deals */}
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Recent</div>
                {recentDeals.map((d, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: i < recentDeals.length - 1 ? '1px solid var(--border)' : 'none' }}>
                    <div style={{ fontSize: 12, fontWeight: 500, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.brand}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, marginLeft: 8 }}>
                      {!hide && <StatusBadge status={d.status} />}
                      {!hide && <span style={{ fontSize: 12, fontWeight: 700, fontFamily: 'var(--font-display)' }}>{hide ? '•••••' : fmt(d.deal_amount)}</span>}
                      {hide && <span style={{ fontSize: 11, color: 'var(--text3)' }}>{d.month}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
